import React, { useMemo } from "react";
import { ListAlt } from "@mui/icons-material";
import { InputAdornment, ListSubheader, MenuItem, TextField, Tooltip } from "@mui/material";

import { selectVersionsByProjectId } from "fond/api";
import useProjectHasScenarioConfiguration from "fond/hooks/useProjectHasScenarioConfiguration";
import { useRightBarContext } from "fond/map/RightBar";
import { Version } from "fond/types";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { isNBNProject } from "fond/utils/project";

import { getCurrentProject, setVersion } from "../../project/redux";

import { Container, IconButton } from "./RightBar.styles";
import { FormField, VersionChip } from "./VersionPicker.styles";

type VersionGroup = {
  title: string;
  items: Version[];
};

const VersionPicker: React.FC = () => {
  const dispatch = useAppDispatch();
  const { expanded, setExpanded } = useRightBarContext();
  const projectId = useAppSelector((state) => state.project.projectId);
  const versionId = useAppSelector((state) => state.project.versionId);
  const versions = useAppSelector((state) => selectVersionsByProjectId(state, projectId));
  const project = useAppSelector((state) => getCurrentProject(state.project));
  const { hasConfig: hasScenarioConfiguration } = useProjectHasScenarioConfiguration({ projectId });
  const isCostToServeProject = hasScenarioConfiguration || (isNBNProject(project) && project.Subtype === "nbn_cost_to_serve");
  /**
   * Determines the chip label for the current version based on the project type.
   */
  const label = useMemo(() => {
    if (isCostToServeProject) {
      if (versions?.at(-1)?.ID === versionId) return "Design";
      return "Scenario";
    }
    return "Version";
  }, [isCostToServeProject, versionId, versions]);

  /**
   * Groups versions into Design and Scenarios for NBN projects,
   * or a single Versions group for other projects.
   */
  const groups: Array<VersionGroup> = useMemo(() => {
    if (!versions) return [];

    if (isCostToServeProject) {
      return [
        {
          title: "Design",
          items: versions ? [versions.at(-1)] : [],
        },
        { title: "Scenarios", items: versions ? versions.slice(0, -1) : [] },
      ] as VersionGroup[];
    }
    return [{ title: "Versions", items: versions || [] }];
  }, [isCostToServeProject, versions]);

  /**
   * Handles the change event from the select input.
   * @param event The change event from the select input.
   */
  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newVersionId = event.target.value;
    if (newVersionId !== versionId) {
      dispatch(setVersion(newVersionId));
    }
  };

  /**
   * Toggles the expanded state of the version picker.
   * If currently expanded, it collapses; otherwise, it expands to show versions.
   */
  const toggleExpand = () => {
    setExpanded(expanded === "versions" ? "none" : "versions");
  };

  return (
    <Container>
      {expanded === "versions" && (
        <FormField>
          <TextField
            select
            variant="outlined"
            fullWidth
            value={versionId}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <VersionChip label={label} size="small" />
                </InputAdornment>
              ),
            }}
            onChange={handleOnChange}
          >
            {groups.map(({ title, items }) => [
              ...(groups.length > 1 ? [<ListSubheader key={title}>{title}</ListSubheader>] : []),
              items?.map(({ ID, Name }) => [
                <MenuItem value={ID} key={ID} sx={{ pl: groups.length > 1 ? 4 : 2 }}>
                  {Name}
                </MenuItem>,
              ]),
            ])}
          </TextField>
        </FormField>
      )}
      <Tooltip title="Versions" placement="bottom" arrow>
        <IconButton aria-label="version" size="small" onClick={toggleExpand} sx={{ width: 28 }}>
          <ListAlt sx={{ width: "1.25rem", height: "1.25rem" }} />
        </IconButton>
      </Tooltip>
    </Container>
  );
};
export default VersionPicker;
